import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Tags,
  ShoppingCart,
  ArrowRight,
  Check,
  ChevronRight,
  Globe,
  Zap,
} from 'lucide-react';
import { platformBranding } from '@/config/branding';

const modules = [
  {
    icon: Tags,
    name: 'LabelForge',
    desc: 'Create beautiful product labels with QR codes',
  },
  {
    icon: ShoppingCart,
    name: 'ShopFather',
    desc: 'Manage products and orders in one place',
  },
  {
    icon: Globe,
    name: 'HTML Storefront',
    desc: 'Generate a stunning website for your store',
  },
  {
    icon: Zap,
    name: 'Instant Setup',
    desc: 'Your online store ready in minutes',
  },
];

const plans = [
  {
    name: 'Starter',
    price: '0',
    period: 'forever',
    features: ['1 Store', 'Up to 10 products', 'Basic HTML storefront'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Professional',
    price: '29',
    period: '/month',
    features: ['3 Stores', 'Unlimited products', 'Premium templates', 'Priority support'],
    cta: 'Start Trial',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: '99',
    period: '/month',
    features: ['Unlimited stores', 'Custom templates', 'API access', 'White-label'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200">
        <div className="bg-white/80 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#4A7C2E] flex items-center justify-center font-bold text-white text-xs">
                  GG
                </div>
                <span className="text-lg font-semibold text-slate-900 tracking-tight">
                  {platformBranding.platform.fullName}
                </span>
              </div>
              
              <nav className="hidden md:flex items-center gap-8">
                <a href="#modules" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                  Modules
                </a>
                <a href="#pricing" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">
                  Pricing
                </a>
              </nav>

              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="text-sm px-4 py-2 bg-[#4A7C2E] text-white font-medium rounded-lg hover:bg-[#3A5F1E] transition-all"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-xs text-green-700 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961]" />
                Online Store Builder
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6"
            >
              {platformBranding.platform.fullName}
              <br />
              <span className="text-slate-500">Your Complete Business Suite</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-lg text-slate-600 mb-10 max-w-xl"
            >
              Complete business suite with ShopFather, LabelForge & SealyDoc. 
              No coding required - professional tools for your business.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="flex items-center gap-4"
            >
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A7C2E] text-white font-semibold rounded-lg hover:bg-[#3A5F1E] transition-all"
              >
                Start Free
                <ArrowRight size={16} />
              </Link>
              <a
                href="#modules"
                className="inline-flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 transition-colors border border-gray-300 rounded-lg hover:border-gray-400 bg-white"
              >
                Learn More
                <ChevronRight size={16} />
              </a>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 pt-12 border-t border-gray-200"
          >
            {[
              { value: '1,000+', label: 'Stores Created' },
              { value: '30+', label: 'Countries' },
              { value: '50K+', label: 'Products Listed' },
              { value: '100K+', label: 'Labels Generated' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Everything you need
            </h2>
            <p className="text-slate-600 max-w-xl">
              Simple tools to create and manage your online store
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {modules.map((module, index) => (
              <motion.div
                key={module.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group p-6 rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#4A7C2E] flex items-center justify-center">
                    <module.icon size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">
                      {module.name}
                    </h3>
                    <p className="text-sm text-slate-600">{module.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Simple pricing
            </h2>
            <p className="text-slate-600">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl border transition-all hover:shadow-md ${
                  plan.highlight
                    ? 'bg-white border-green-300 shadow-md'
                    : 'bg-white border-gray-200'
                }`}
              >
                {plan.highlight && (
                  <div className="text-xs text-green-700 font-semibold mb-4 inline-flex px-2 py-1 rounded-full bg-green-50 border border-green-200">
                    RECOMMENDED
                  </div>
                )}
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-3xl font-bold text-slate-900">€{plan.price}</span>
                  <span className="text-sm text-slate-600">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check size={16} className="text-[#4A7C2E]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`block w-full py-2.5 text-sm font-semibold text-center rounded-lg transition-all ${
                    plan.highlight
                      ? 'bg-[#4A7C2E] text-white hover:bg-[#3A5F1E]'
                      : 'bg-white text-slate-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-4 mt-8 text-sm text-slate-500">
            <span>Accepted:</span>
            <span className="text-slate-700">BTC</span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-700">ETH</span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-700">USDT</span>
            <span className="text-slate-600">•</span>
            <span className="text-slate-700">Card</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Ready to start?
          </h2>
          <p className="text-slate-600 mb-8">
            Join businesses using {platformBranding.platform.fullName} for their daily operations
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A7C2E] text-white font-semibold rounded-lg hover:bg-[#3A5F1E] transition-all"
          >
            Create Free Account
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-[#4A7C2E] flex items-center justify-center font-bold text-white text-[10px]">
                GG
              </div>
              <span className="text-sm font-medium text-slate-900">{platformBranding.platform.fullName}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <a href="#" className="hover:text-slate-900 transition-colors">Terms</a>
              <a href="#" className="hover:text-slate-900 transition-colors">Privacy</a>
              <a href={`mailto:${platformBranding.platform.supportEmail}`} className="hover:text-slate-900 transition-colors">Contact</a>
            </div>
            <div className="text-center">
              <p className="text-sm text-slate-600">
                © 2026 {platformBranding.platform.fullName}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Powered by{' '}
                <a 
                  href={platformBranding.poweredBy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4A7C2E] hover:text-[#3A5F1E] transition-colors"
                >
                  {platformBranding.poweredBy.name}
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
